import React, { ReactNode } from 'react';
import {
  AvJsonViewer,
  AvProgress,
  AvTag,
  AvTagsList,
  AvTooltip,
  SEVERITY_LABELS,
  TColumnsDef,
  VulCountBySeverity,
} from '@fabric-ui/components';
import { FormatterType, HeadCellType } from '@fabric-ui/components/Table';
import { DrillDown, Findings, Ticket } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, ellipsis, mapAliasesAndDims, mapAliasesAndMetrics } from '@fabric-ui/utils';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import { CountByEntities } from '@utils/entities.utils';
import { entityViewConfig } from '@utils/entityViewConfig';
import { FINDING_STATE_LABELS } from '@utils/filterValueLabels';
import { getAssetTypeIcon, isJSON, isValidHttpUrl, truncateDecimal } from '@utils/utils';
import { format, isPast } from 'date-fns';
import AvItemWithLogoGroup, { IconSize } from '../../components/AvItemWithLogoGroup';
import { IconVariant, SeverityItem } from '../../components/ItemWithLogo';
import { useGetUsersInfo } from '../../hooks/useGetUsersInfo';
import { isDebugMode } from '../../utils/rum.utils';
import { FieldType, FieldTypeEnum } from '../Sources/Mapping/types';
import ScoreExplanation from './ScoreExplanation';
import IncidentTypeFormatter from './Threats/IncidentTypeFormatter';
import {
  ALERT_SEVERITY,
  ALERT_SEVERITY_SCORE,
  EXCEPTION_REQUESTER_EMAIL,
  EXCEPTION_REQUESTER_NAME,
  EXCEPTION_REVIEWER_EMAIL,
  EXCEPTION_REVIEWER_NAME,
  ExceptionScope,
  ExceptionType,
  FINDING_ORIGINAL_SEVERITY,
  FINDING_ORIGINAL_SEVERITYSCORE,
  FINDING_SEVERITY,
  FINDING_SEVERITY_SCORE,
  formatAsUTC,
  POLICY_VIOLATION_SEVERITY,
  POLICY_VIOLATION_SEVERITY_SCORE,
  SCORE_EXPLANATION_FIELD,
} from './ticket.types';

export const severityCell = ({
  value,
  width = 24,
  showText = true,
  severity,
}: {
  value?: number;
  width?: number;
  showText?: boolean;
  severity: string;
}) => <SeverityItem width={width} value={severity} numericValue={value} showText={showText} />;

export const getStateValue = (state: string | boolean | null | undefined): string =>
  FINDING_STATE_LABELS[typeof state === 'boolean' ? (state ? 'INACTIVE' : 'ACTIVE') : (state ?? '')] ?? 'Undetected';

export function StatusCell({ value, lastSeen }: { value: string; lastSeen?: string }) {
  const isActive = value === 'Active';
  return (
    <AvTooltip
      title={!isActive ? (lastSeen ? `Last Seen on ${new Date(lastSeen)?.toLocaleString()}` : 'Undetected in the latest scan') : ''}>
      <span>
        <AvTag variant={isActive ? 'primary' : 'green'} text={value} />
      </span>
    </AvTooltip>
  );
}

export const drilldownIcon = <DrillDown style={{ transform: 'rotate(-90deg)', marginTop: '-4px' }} />;

const getTotalFindingsCellComponent = (v, row, includeNone?: boolean): ReactNode => (
  <Box sx={{ ...flex.itemsEnd, gap: 1.5 }}>
    <Box
      sx={{
        fontWeight: 600,
        whiteSpace: 'nowrap',
        minWidth: 40,
        width: 40,
        textAlign: 'end',
      }}>
      {abbreviateNumber(v)}
    </Box>
    <VulCountBySeverity data={row} includeNone={includeNone} />
  </Box>
);

const MitreCell = ({ v }: { v: { id: string; name: string }[] }) => <div>{v.map(o => o.name).join(', ')}</div>;
const dateValueFormat = value => (value ? format(new Date(value), 'MMM dd, yyyy') : value);

const exceptionTypeMap = {
  [ExceptionType.SLA_EXTENSION]: 'SLA Extension',
  [ExceptionType.RISK_ACCEPTED]: 'Risk Accepted',
};

const exceptionScopeMap = {
  [ExceptionScope.RULE_BASED]: 'Rule Based',
  [ExceptionScope.SPECIFIC_ENTITY]: 'Specific Entity',
};

const EXCEPTIONS_ENTITY_CONFIG = {
  tickets: {
    icon: <Ticket />,
    label: 'Ticket',
  },
  findings: {
    icon: <Findings />,
    label: 'Finding',
  },
} as const;

const AffectedEntityCell = ({ value }: { value: string }) => {
  const config = EXCEPTIONS_ENTITY_CONFIG[value as keyof typeof EXCEPTIONS_ENTITY_CONFIG];

  return (
    <Box sx={{ ...flex.itemsStart, gap: 1 }}>
      {config?.icon}
      {config?.label || value}
    </Box>
  );
};

export const riskMassHeadcell: HeadCellType = {
  id: 'active_findings_risk_mass_granular',
  label: 'Risk Mass',
  type: FormatterType.number,
  formatter: v => (v ? v?.toLocaleString() : 0),
};

export const riskMassHeadcellV2 = {
  id: 'active_findings_risk_mass_granular',
  header: 'Risk Mass',
  cell: info => {
    const v = info.getValue();
    return v ? v?.toLocaleString() : 0;
  },
  meta: { type: FormatterType.number },
};

export const getSlaFormatter = ({ value, row = {}, pathAlias = '' }) => {
  const dateValue = new Date(value);
  const isInPast = value && isPast(dateValue);
  const isOverSla = isInPast && row[`${pathAlias}.state`] === 'ACTIVE';
  return (
    <Box sx={{ ...(isOverSla && { color: theme => theme.palette.error.main }) }}>{value ? formatAsUTC(value, false, true) : 'No SLA'}</Box>
  );
};

const getFindingsViewSlaHeadcells = (isTableV2 = false) => {
  if (isTableV2) {
    return [
      {
        id: 'finding.sla',
        header: 'Finding SLA',
        size: 120,
        cell: info => getSlaFormatter({ value: info.getValue(), row: info.row.original, pathAlias: 'finding' }),
      },
      {
        id: 'ticket.sla',
        header: 'Ticket SLA',
        size: 120,
        cell: info => getSlaFormatter({ value: info.getValue(), row: info.row.original, pathAlias: 'finding' }),
      },
    ];
  }
  return [
    {
      id: 'finding.sla',
      label: 'Finding SLA',
      initWidth: 120,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'finding' }),
      getValue: row => (row['finding.sla'] ? formatAsUTC(row['finding.sla'], false, true) : 'No SLA'),
    },
    {
      id: 'ticket.sla',
      label: 'Ticket SLA',
      initWidth: 120,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'finding' }),
      getValue: row => (row['ticket.sla'] ? formatAsUTC(row['ticket.sla'], false, true) : 'No SLA'),
    },
  ];
};

const getAdvancedExceptionsHeadcells = (isTableV2 = false) => {
  if (isTableV2) {
    return [
      {
        id: 'exception.type',
        header: 'Type',
        cell: ({ getValue }) => exceptionTypeMap[getValue() as string] || getValue(),
      },
      {
        id: 'exception.scope',
        header: 'Scope',
        cell: ({ getValue }) => exceptionScopeMap[getValue() as string] || getValue(),
      },
      {
        id: 'exception.related_proj_name',
        header: 'Affected Entity',
        cell: ({ getValue }) => <AffectedEntityCell value={(getValue() as string) || ''} />,
      },
    ];
  }
  return [
    {
      id: 'exception.type',
      label: 'Type',
      formatter: v => exceptionTypeMap[v as string] || v,
    },
    {
      id: 'exception.scope',
      label: 'Scope',
      formatter: v => exceptionScopeMap[v as string] || v,
    },
    {
      id: 'exception.related_proj_name',
      label: 'Affected Entity',
      formatter: v => <AffectedEntityCell value={v} />,
    },
  ];
};

export const AssigneeCell = ({ assigneeId }: { assigneeId: string }) => {
  const { usersInfo, isLoading } = useGetUsersInfo([assigneeId], undefined, {}, !!assigneeId && assigneeId !== 'None');
  if (isLoading) return null;
  const displayName = usersInfo[assigneeId] ?? assigneeId ?? 'No Assignee';
  return <AvTooltip>{displayName}</AvTooltip>;
};

// TODO: move tableHeadCellsMap to entityViewConfig
export const tableHeadCellsMap = (isThreatApp = false, findingsViewSlaFF = false, isAdvancedExceptionsEnabled = false) => ({
  [entityViewConfig.Ticket.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'ticket._key' },
    { id: 'ticket.type', label: 'Type', hidden: !isDebugMode() },
    {
      id: 'ticket.severity_score',
      initWidth: 140,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row['ticket.severity'],
        }),
      label: 'Severity Score',
      getValue: row => `${row['ticket.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['ticket.severity']]}`,
    },
    riskMassHeadcell,
    {
      id: 'ticket.title',
      label: 'Title',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      type: 'string',
    },
    {
      id: 'ticket.first_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Discovered',
      getValue: row => (row[`ticket.first_seen`] ? format(new Date(row[`ticket.first_seen`]), 'MMM dd, yyyy') : row[`ticket.first_seen`]),
    },
    {
      id: mapAliasesAndDims[entityViewConfig.Ticket.entityTypeId].sla.alias,
      label: 'SLA',
      initWidth: 120,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'ticket' }),
      getValue: row => (row.sla ? formatAsUTC(row.sla, false, true) : 'No SLA'),
    },
    {
      id: `ticket.assignee_id`,
      label: 'Assignee',
      getValue: ({ [`ticket.assignee_id`]: owner }) => owner || 'No Assignee',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: `ticket.source_names`,
      label: 'Sources',
      formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
    },
    {
      id: `ticket.current_status.name`,
      label: 'Status',
      formatter: v => <AvTag text={v} oneLine />,
    },
    {
      id: `ticket.integration_info.key`,
      label: 'External ticket ID',
      hidden: true,
    },
    {
      id: 'remediation',
      label: 'Remediation',
      formatter: v => <AvProgress width={50} value={truncateDecimal(v, 1)} />,
      getValue: row => `${Math.round(row.remediation)}%`,
    },
    {
      id: 'ticket.finding_count',
      label: 'Findings',
      type: 'number',
      style: { width: 50 },
      formatter: v => (v ? v?.toLocaleString() : ''),
    },
    {
      id: 'ticket.asset_count',
      label: 'Assets',
      type: 'number',
      style: { width: 50 },
      formatter: v => (v ? v?.toLocaleString() : ''),
    },
  ],
  [entityViewConfig.Alert.entityTypeId]: isThreatApp
    ? [
        { isKey: true, label: 'ID', id: 'alert._key' },
        {
          id: `alert.severity`,
          initWidth: 140,
          formatter: (v, row) =>
            severityCell({
              value: undefined,
              width: 24,
              severity: row[`alert.severity`],
            }),
          label: 'Severity Score',
          getValue: row => SEVERITY_LABELS[row[`alert.severity`]],
        },
        {
          id: `alert.severity_score`,
          initWidth: 140,
          type: 'number',
        },
        {
          id: 'alert.title',
          label: 'Alert Title',
          type: 'string',
        },
        {
          id: `alert.first_seen`,
          label: 'Date',
          type: FormatterType.date,
        },
        {
          id: `alert.source_names`,
          label: 'Sources',
          formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
        },
        {
          id: 'mitre_tactics',
          label: 'Tactic',
          formatter: (v: string[] | string) => (Array.isArray(v) ? v?.join(', ') : v),
        },
        {
          id: 'mitre_techniques',
          label: 'Technique',
          formatter: (v: string[] | string) => (Array.isArray(v) ? v?.join(', ') : v),
        },
        {
          id: `alert.verdict`,
          label: 'Verdict',
          type: FormatterType.string,
        },
        {
          id: `alert.original_severity`,
          label: 'Original Severity',
          type: FormatterType.string,
        },
      ]
    : [
        { isKey: true, label: 'ID', id: 'alert._key' },
        {
          id: `alert.severity_score`,
          initWidth: 140,
          formatter: (v, row) =>
            severityCell({
              value: undefined,
              width: 24,
              severity: row[`alert.severity`],
            }),
          label: 'Severity Score',
          getValue: row => SEVERITY_LABELS[row[`alert.severity`]],
        },
        {
          id: `alert.created`,
          label: 'Date',
          type: FormatterType.date,
        },
        {
          id: 'alert.rule_name',
          label: 'Rule Name',
        },
        {
          id: `alert.last_seen`,
          label: 'Last Seen',
          type: FormatterType.date,
        },
        {
          id: 'alert.title',
          label: 'Alert Title',
          type: 'string',
        },
        {
          id: `alert.source_names`,
          label: 'Sources',
          formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
        },
        {
          id: 'alert.tactic_name',
          label: 'Tactic',
        },
        {
          id: 'alert.technique_name',
          label: 'Technique',
        },
        {
          id: 'alert.state',
          label: 'State',
        },
        {
          id: 'alert.tags',
          label: 'Tags',
          formatter: values => (Array.isArray(values) ? <AvTagsList tags={values} numOfTags={1} /> : ''),
        },
      ],
  [entityViewConfig.Incident.entityTypeId]: isThreatApp // TODO: should change once old incident page will be removed
    ? [
        { isKey: true, label: 'ID', id: 'incident._key' },
        {
          id: `incident.severity`,
          initWidth: 140,
          formatter: (v, row) =>
            severityCell({
              value: undefined,
              width: 24,
              severity: row[`incident.severity`],
            }),
          label: 'Severity',
          getValue: row => `${row.incident_risk_mass_granular?.toFixed(1)} ${SEVERITY_LABELS[row[`incident.severity`]]}`,
        },
        {
          id: `incident_risk_mass_granular`,
          label: 'Risk Mass',
          type: 'number',
          formatter: v => (v ? v?.toLocaleString() : 0),
        },
        {
          id: `incident.type`,
          label: 'Type',
          formatter: v => <IncidentTypeFormatter value={v} />,
        },
        {
          id: `incident.title`,
          label: 'Name',
          hasTooltip: true,
          style: { maxWidth: 0 },
          initWidth: 200,
          type: 'string',
        },
        {
          id: `incident.created`,
          formatter: v => {
            if (!v) return v;
            const date = new Date(v);
            return Number.isNaN(date.getTime()) ? v : format(date, 'MMM dd, yyyy');
          },
          label: 'Time Created',
          getValue: row => {
            const v = row[`incident.created`];
            if (!v) return v;
            const date = new Date(v);
            return Number.isNaN(date.getTime()) ? v : format(date, 'MMM dd, yyyy');
          },
        },
        {
          id: 'incident.alert_count',
          label: 'Total Alerts',
          type: 'number',
          style: { width: 50 },
          formatter: v => (v ? v?.toLocaleString() : 0),
        },
        {
          id: `incident.source_names`,
          label: 'Sources',
          formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
        },
        {
          id: `incident.assignee_id`,
          label: 'Assignee',
          formatter: v => <AssigneeCell assigneeId={v} />,
          style: { maxWidth: 0 },
        },
        {
          id: `incident.current_status.name`,
          label: 'Status',
          formatter: v => <AvTag text={v} oneLine />,
        },
        {
          id: `incident.integration_info.key`,
          label: 'External ticket ID',
          hidden: true,
        },
      ]
    : [
        { isKey: true, label: 'ID', id: 'incident._key' },
        { id: `incident.type`, label: 'Type', hidden: !isDebugMode() },
        {
          id: `incident.severity_score`,
          initWidth: 140,
          formatter: (v, row) =>
            severityCell({
              value: undefined,
              width: 24,
              severity: row[`incident.severity`],
            }),
          label: 'Severity Score',
          getValue: row => `${row[`incident.severity_score`]?.toFixed(1)} ${SEVERITY_LABELS[row[`incident.severity`]]}`,
        },
        {
          id: `incident.title`,
          label: 'Title',
          hasTooltip: true,
          style: { maxWidth: 0 },
          initWidth: 200,
          type: 'string',
        },
        {
          id: `incident.first_seen`,
          formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
          label: 'First Seen',
          getValue: row =>
            row[`incident.first_seen`] ? format(new Date(row[`incident.first_seen`]), 'MMM dd, yyyy') : row[`incident.first_seen`],
        },
        {
          id: mapAliasesAndDims[entityViewConfig.Incident.entityTypeId].sla.alias,
          label: 'SLA',
          initWidth: 120,
          formatter: v => {
            const dateValue = new Date(v);
            const isInPast = v && isPast(dateValue);
            return <Box sx={{ ...(isInPast && { color: theme => theme.palette.error.main }) }}>{v ? formatAsUTC(v, true) : 'No SLA'}</Box>;
          },
          getValue: row => (row.sla ? formatAsUTC(row.sla, true) : 'No SLA'),
        },
        {
          id: `incident.assignee_id`,
          label: 'Assignee',
          formatter: v => <AssigneeCell assigneeId={v} />,
          style: { maxWidth: 0 },
        },
        {
          id: `incident.source_names`,
          label: 'Sources',
          formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
        },
        {
          id: `incident.current_status.name`,
          label: 'Status',
          formatter: v => <AvTag text={v} oneLine />,
        },
        {
          id: `incident.integration_info.key`,
          label: 'External ticket ID',
          hidden: true,
        },
      ],
  [entityViewConfig.Asset.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'asset._key' },
    {
      id: 'asset.type',
      label: 'Type',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      formatter: v => {
        const icon = getAssetTypeIcon(v);
        return (
          <Box sx={{ ...flex.itemsStart, gap: 1, ...ellipsis }}>
            {icon}
            <AvTooltip>{v}</AvTooltip>
          </Box>
        );
      },
    },
    {
      id: 'asset.name',
      label: 'Name',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: mapAliasesAndMetrics.severityScore.alias,
      label: 'Risk Score',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row.severity,
        }),
    },
    riskMassHeadcell,
    {
      id: 'asset.owner_id',
      label: 'Owner',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: `asset.source_names`,
      label: 'Sources',
      formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
    },
    {
      id: 'asset.crown_jewel',
      label: 'Crown Jewel',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: 'asset.site',
      label: 'Site',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: 'asset.first_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'First Seen',
      getValue: row => (row[`asset.first_seen`] ? format(new Date(row[`asset.first_seen`]), 'MMM dd, yyyy') : row[`asset.first_seen`]),
    },
    {
      id: 'asset.last_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Seen',
      getValue: row => (row[`asset.last_seen`] ? format(new Date(row[`asset.last_seen`]), 'MMM dd, yyyy') : row[`asset.last_seen`]),
    },
    {
      id: mapAliasesAndMetrics.totalFindings.alias,
      label: 'Total Findings',
      hasTooltip: true,
      initWidth: 400,
      formatter: (v, row) => getTotalFindingsCellComponent(v, row),
    },
    {
      id: 'asset.tags',
      label: 'Tags',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
  ],
  [entityViewConfig.Finding.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'finding._key' },
    { id: 'finding.type', label: 'Type', hidden: !isDebugMode() },
    {
      id: 'finding.severity_score',
      initWidth: 140,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row['finding.severity'],
        }),
      label: 'Severity Score',
      getValue: row => `${row['finding.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['finding.severity']]}`,
    },
    {
      id: `finding.title`,
      label: 'Title',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      type: 'string',
    },
    {
      id: `finding.source_names`,
      label: 'Source',
      formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
    },
    {
      id: `asset._key`,
      label: 'Asset Key',
      hasTooltip: true,
      type: 'string',
    },
    {
      id: `asset.type`,
      label: 'Asset Type',
      hasTooltip: true,
      type: 'string',
    },
    {
      id: `finding.cve_id`,
      label: 'CVE',
      hasTooltip: true,
    },
    {
      id: `finding.first_seen`,
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'First Seen',
      getValue: row =>
        row[`finding.first_seen`] ? format(new Date(row[`finding.first_seen`]), 'MMM dd, yyyy') : row[`finding.first_seen`],
    },
    {
      id: `finding.last_seen`,
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Seen',
      getValue: row => (row[`finding.last_seen`] ? format(new Date(row[`finding.last_seen`]), 'MMM dd, yyyy') : row[`finding.last_seen`]),
    },
    {
      id: `component.name`,
      label: 'Component',
      hasTooltip: true,
      type: 'string',
    },
    {
      id: `finding.state`,
      label: 'State',
      formatter: (v, row) => <StatusCell value={getStateValue(v)} lastSeen={row[`finding.last_seen`]} />,
      getValue: row => getStateValue(row['finding.state']),
    },
    ...(findingsViewSlaFF ? getFindingsViewSlaHeadcells() : []),
  ],
  [entityViewConfig.PolicyViolation.entityTypeId]: [
    { isKey: true, id: 'policy_violation._key', label: 'ID' },
    {
      id: 'policy_violation.severity_score',
      initWidth: 140,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row['policy_violation.severity'],
        }),
      label: 'Severity Score',
      getValue: row => `${row['policy_violation.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['policy_violation.severity']]}`,
    },
    { id: 'policy_violation.title', label: 'Title' },
    { id: 'policy_violation.asset.name', label: 'Asset Name' },
    {
      id: 'policy_violation.asset.type',
      label: 'Asset Type',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      formatter: v => {
        const icon = getAssetTypeIcon(v);
        return (
          <Box sx={{ ...flex.itemsStart, gap: 1, ...ellipsis }}>
            {icon}
            <AvTooltip>{v}</AvTooltip>
          </Box>
        );
      },
    },
    {
      id: 'policy_violation.first_seen',
      label: 'First Seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      getValue: row =>
        row[`policy_violation.first_seen`]
          ? format(new Date(row[`policy_violation.first_seen`]), 'MMM dd, yyyy')
          : row[`policy_violation.first_seen`],
    },
    {
      id: 'policy_violation.last_seen',
      label: 'Last Seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      getValue: row =>
        row[`policy_violation.first_seen`]
          ? format(new Date(row[`policy_violation.first_seen`]), 'MMM dd, yyyy')
          : row[`policy_violation.first_seen`],
    },
    {
      id: 'policy_violation.state',
      label: 'State',
      formatter: (v, row) => <StatusCell value={getStateValue(v)} lastSeen={row['policy_violation.last_seen']} />,
    },
  ],
  [entityViewConfig.ViolationTicket.entityTypeId]: [
    { isKey: true, id: 'violation_ticket._key', label: 'ID' },
    {
      id: 'violation_ticket.severity_score',
      initWidth: 140,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row['violation_ticket.severity'],
        }),
      label: 'Severity Score',
      getValue: row => `${row['violation_ticket.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['violation_ticket.severity']]}`,
    },
    { id: 'violation_ticket.title', label: 'Title' },
    {
      id: 'violation_ticket.sla',
      label: 'SLA',
      initWidth: 120,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'ticket' }),
      getValue: row => (row.sla ? formatAsUTC(row.sla, false, true) : 'No SLA'),
    },
    {
      id: `violation_ticket.assignee_id`,
      label: 'Assignee',
      getValue: ({ [`violation_ticket.assignee_id`]: owner }) => owner || 'No Assignee',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: `violation_ticket.current_status`,
      label: 'Status',
      formatter: v => <AvTag text={v} oneLine />,
    },
    {
      id: 'violation_remediation_percentage_granular',
      label: 'Remediation',
      formatter: v => <AvProgress width={50} value={truncateDecimal(v, 1)} />,
      getValue: row => `${Math.round(row.violation_remediation_percentage_granular)}%`,
    },
    {
      id: 'total_policy_violations_granular',
      label: 'Policy Violation Count',
      type: 'number',
      style: { width: 50 },
      formatter: v => (v ? v?.toLocaleString() : ''),
    },
    {
      id: 'total_assets_granular',
      label: 'Asset Count',
      type: 'number',
      style: { width: 50 },
      formatter: v => (v ? v?.toLocaleString() : ''),
    },
  ],
  [entityViewConfig.Exception.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'exception._key' },
    { id: 'exception.current_status', label: 'Status', formatter: v => <AvTag text={v} oneLine /> },
    { id: 'exception.reason', label: 'Reason' },
    {
      id: 'exception.requested_sla',
      label: 'Requested SLA',
      type: FormatterType.date,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'exception' }),
    },
    {
      id: 'ticket.sla',
      label: 'Current SLA',
      type: FormatterType.date,
      formatter: (v, row) => getSlaFormatter({ value: v, row, pathAlias: 'exception' }),
    },
    {
      id: EXCEPTION_REQUESTER_EMAIL,
      label: 'Requester',
      getValue: row => row[EXCEPTION_REQUESTER_NAME] || row[EXCEPTION_REQUESTER_EMAIL],
    },
    {
      id: 'ticket.severity_score',
      initWidth: 140,
      label: 'Ticket Severity Score',
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row['ticket.severity'],
        }),
      getValue: row => `${row['ticket.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['ticket.severity']]}`,
    },
    { id: 'ticket.title', label: 'Ticket Title' },
    { id: 'ticket._key', label: 'Ticket ID' },
    ...(isAdvancedExceptionsEnabled ? getAdvancedExceptionsHeadcells(false) : []),
    {
      id: EXCEPTION_REVIEWER_EMAIL,
      label: 'Reviewer',
      getValue: row => row[EXCEPTION_REVIEWER_NAME] || row[EXCEPTION_REVIEWER_EMAIL],
    },
  ],
  [entityViewConfig.Tenant.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'tenant._key' },
    {
      id: 'tenant.name',
      label: 'Name',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: 'tenant.severity_score',
      label: 'Severity Score',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      formatter: (v, row) =>
        severityCell({
          value: Number(v),
          width: 24,
          severity: row['tenant.severity'],
        }),
    },
    {
      id: 'totalFindings',
      label: 'Total Identity Findings',
      hasTooltip: true,
      initWidth: 400,
      formatter: (v, row) => getTotalFindingsCellComponent(v, row, false),
    },
    {
      id: 'tenant.last_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Seen',
      getValue: row => dateValueFormat(row['tenant.last_seen']),
    },
    {
      id: 'tenant.state',
      label: 'State',
      formatter: (v, row) => <StatusCell value={getStateValue(v)} lastSeen={row['tenant.last_seen']} />,
    },
  ],
  [entityViewConfig.IdentityFinding.entityTypeId]: [
    {
      isKey: true,
      label: 'ID',
      id: 'identity_finding._key',
    },
    {
      id: 'identity_finding.severity_score',
      label: 'Severity',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
      formatter: (v, row) =>
        severityCell({
          value: v,
          width: 24,
          severity: row.severity,
        }),
    },
    {
      id: 'title',
      label: 'Title',
      hasTooltip: true,
      style: { maxWidth: 0 },
    },
    {
      id: 'tenantName',
      label: 'Tenant',
      hasTooltip: true,
    },
    {
      id: 'affected_entities',
      label: 'Affected Entities',
      hasTooltip: true,
      initWidth: 200,
      formatter: (v, row) => <CountByEntities data={row} />,
    },
    {
      id: 'identity_finding.exploit_ease',
      label: 'Exploit Ease',
      hasTooltip: true,
    },
    {
      id: 'mitreTactics',
      label: 'Tactics',
      formatter: (v: { id: string; name: string }[]) => <MitreCell v={v} />,
    },
    {
      id: 'mitreTechniques',
      label: 'Techniques',
      formatter: (v: { id: string; name: string }[]) => <MitreCell v={v} />,
    },
    {
      id: 'identity_finding.last_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Seen',
      getValue: row => dateValueFormat(row['identity_finding.last_seen']),
    },
    {
      id: 'identity_finding.remediation_ease',
      label: 'Remediation Ease',
      hasTooltip: true,
    },
  ],
  [entityViewConfig.User.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'user._key' },
    {
      id: 'user.user_name',
      label: 'User Name',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: 'user.login',
      label: 'Login',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: 'user.source_key',
      label: 'Login ID',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: 'user.email',
      label: 'Email',
    },
    {
      id: 'user.type',
      label: 'Type',
      hasTooltip: true,
    },
    {
      id: 'user.identity_type',
      label: 'Identity Type',
      hasTooltip: true,
    },
    {
      id: 'totalFindings',
      label: 'Total Identity Findings',
      hasTooltip: true,
      initWidth: 400,
      formatter: (v, row) => (
        <Box sx={{ ...flex.itemsEnd, gap: 1.5 }}>
          <Box
            sx={{
              fontWeight: 600,
            }}>
            {abbreviateNumber(v)}
          </Box>
          <VulCountBySeverity data={row} includeNone={false} />
        </Box>
      ),
    },
    {
      id: 'total_alerts',
      label: 'Alerts',
      hasTooltip: true,
    },
    {
      id: 'total_assets',
      label: 'Assets',
      hasTooltip: true,
    },
    {
      id: 'applications',
      label: 'Applications',
      hasTooltip: true,
    },
    {
      id: 'roles',
      label: 'Roles',
      hasTooltip: true,
    },
    {
      id: 'app_roles',
      label: 'Application Roles',
      hasTooltip: true,
    },
    {
      id: 'groups',
      label: 'Groups',
      hasTooltip: true,
    },
    {
      id: 'user.last_activity_date',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Activity Date',
      getValue: row => dateValueFormat(row[`user.last_activity_date`]),
    },
    {
      id: 'user.last_seen',
      formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v),
      label: 'Last Seen',
      getValue: row => dateValueFormat(row[`user.last_seen`]),
    },
  ],
  [entityViewConfig.Identity.entityTypeId]: [
    { isKey: true, label: 'ID', id: 'identity._key' },
    {
      id: 'identity.user_name',
      label: 'Identity',
      hasTooltip: true,
      style: { maxWidth: 0 },
      initWidth: 200,
    },
    {
      id: 'identity.severity',
      label: 'Risk Level',
      initWidth: 140,
      formatter: v => severityCell({ severity: v, showText: true }),
      getValue: row => SEVERITY_LABELS[row['identity.severity']],
    },
    {
      id: 'identity.severity_score',
      label: 'Risk Score',
      initWidth: 140,
      formatter: (v, row) => severityCell({ value: v, width: 24, severity: row['identity.severity'] }),
      getValue: row => `${row['identity.severity_score']?.toFixed(1)} ${SEVERITY_LABELS[row['identity.severity']]}`,
    },
    {
      id: 'identity.total_findings',
      label: 'Findings',
      type: 'number',
      formatter: v => (v ? v?.toLocaleString() : 0),
    },
    {
      id: 'identity.total_alerts',
      label: 'Alerts',
      type: 'number',
      formatter: v => (v ? v?.toLocaleString() : 0),
    },
    {
      id: 'identity.source_names',
      label: 'Provider',
      formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
    },
    {
      id: 'identity.has_admin_user',
      label: 'Has Admin User',
      formatter: v => <AvTag variant={v ? 'primary' : 'green'} text={v ? 'Yes' : 'No'} />,
      getValue: row => (row['identity.has_admin_user'] ? 'Yes' : 'No'),
    },
    {
      id: 'identity.has_privileged_user',
      label: 'Has Privileged User',
      formatter: v => <AvTag variant={v ? 'primary' : 'green'} text={v ? 'Yes' : 'No'} />,
      getValue: row => (row['identity.has_privileged_user'] ? 'Yes' : 'No'),
    },
    {
      id: 'identity.linked_users',
      label: 'Linked Users',
      type: 'number',
      formatter: v => (v ? v?.toLocaleString() : 0),
    },
    {
      id: 'identity.tenants',
      label: 'Tenants',
      type: 'number',
      formatter: v => (v ? v?.toLocaleString() : 0),
    },
  ],
});

export const tableHeadCellsMapV2 = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isThreatApp = false,
  findingsViewSlaFF = false,
  isAdvancedExceptionsEnabled = false
): Record<string, TColumnsDef> => ({
  [entityViewConfig.Ticket.entityTypeId]: [
    { id: 'ticket._key', header: 'ID', meta: { isKey: true } },
    { id: 'ticket.type', header: 'Type', meta: { hidden: !isDebugMode() } },
    {
      id: 'ticket.severity_score',
      header: 'Severity Score',
      size: 140,
      cell: info =>
        severityCell({
          value: info.getValue() as number,
          width: 24,
          severity: info.row.original['ticket.severity'],
        }),
    },
    riskMassHeadcellV2,
    {
      id: 'ticket.title',
      header: 'Title',
      meta: { type: FormatterType.string },
      size: 200,
    },
    {
      id: 'ticket.first_seen',
      header: 'Discovered',
      cell: info => {
        const v = info.getValue();
        return v ? <AvTooltip>{format(new Date(v as string), 'MMM dd, yyyy')}</AvTooltip> : v;
      },
    },
    {
      id: mapAliasesAndDims[entityViewConfig.Ticket.entityTypeId].sla.alias,
      header: 'SLA',
      size: 120,
      cell: info => <AvTooltip>{getSlaFormatter({ value: info.getValue(), row: info.row.original, pathAlias: 'ticket' })}</AvTooltip>,
    },
    {
      id: `ticket.assignee_id`,
      header: 'Assignee',
    },
    {
      id: `ticket.source_names`,
      header: 'Sources',
      cell: info => (
        <AvItemWithLogoGroup items={(info.getValue() as string[]) || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />
      ),
    },
    {
      id: `ticket.current_status.name`,
      header: 'Status',
      cell: info => <AvTag text={info.getValue() as string} oneLine />,
    },
    {
      id: `ticket.integration_info.key`,
      header: 'External ticket ID',
      meta: { hidden: true },
    },
    {
      id: 'remediation',
      header: 'Remediation',
      cell: info => <AvProgress width={50} value={truncateDecimal(info.getValue(), 1)} />,
    },
    {
      id: 'ticket.finding_count',
      header: 'Findings',
      cell: info => {
        const v = info.getValue();
        return v ? v?.toLocaleString() : 0;
      },
    },
    {
      id: 'ticket.asset_count',
      header: 'Assets',
      cell: info => {
        const v = info.getValue();
        return v ? v?.toLocaleString() : 0;
      },
    },
  ],
  [entityViewConfig.Exception.entityTypeId]: [
    { id: 'exception._key', header: 'ID', meta: { isKey: true } },
    { id: 'exception.current_status', header: 'Status', cell: ({ getValue }) => <AvTag text={getValue() as string} oneLine /> },
    { id: 'exception.reason', header: 'Reason' },
    {
      id: 'exception.requested_sla',
      header: 'Requested SLA',
      meta: {
        type: FormatterType.date,
      },
      cell: ({ getValue, row }) => getSlaFormatter({ value: getValue(), row: row.original, pathAlias: 'exception' }),
    },
    {
      id: 'ticket.sla',
      header: 'Current SLA',
      meta: {
        type: FormatterType.date,
      },
      cell: ({ getValue, row }) => getSlaFormatter({ value: getValue(), row: row.original, pathAlias: 'exception' }),
    },
    {
      id: EXCEPTION_REQUESTER_EMAIL,
      header: 'Requester',
      cell: ({ row }) => <AvTooltip>{row.original[EXCEPTION_REQUESTER_NAME] || row.original[EXCEPTION_REQUESTER_EMAIL]}</AvTooltip>,
    },
    {
      id: 'ticket.severity_score',
      header: 'Ticket Severity Score',
      cell: ({ getValue, row }) =>
        severityCell({
          value: getValue() as number,
          width: 24,
          severity: row.original['ticket.severity'],
        }),
    },
    { id: 'ticket.title', header: 'Ticket Title' },
    { id: 'ticket._key', header: 'Ticket ID' },
    ...(isAdvancedExceptionsEnabled ? getAdvancedExceptionsHeadcells(true) : []),
    {
      id: EXCEPTION_REVIEWER_EMAIL,
      header: 'Reviewer',
      cell: ({ row }) => <AvTooltip>{row.original[EXCEPTION_REVIEWER_NAME] || row.original[EXCEPTION_REVIEWER_EMAIL]}</AvTooltip>,
    },
  ],
  [entityViewConfig.Asset.entityTypeId]: [
    { id: 'asset._key', header: 'ID', meta: { isKey: true } },
    {
      id: 'asset.type',
      header: 'Type',
      size: 200,
      cell: info => {
        const v = info.getValue();
        const icon = getAssetTypeIcon(v);
        return (
          <Box sx={{ ...flex.itemsStart, gap: 1, ...ellipsis }}>
            {icon}
            <AvTooltip>{v}</AvTooltip>
          </Box>
        );
      },
    },
    {
      id: 'asset.name',
      header: 'Name',
      size: 200,
    },
    {
      id: mapAliasesAndMetrics.severityScore.alias,
      header: 'Risk Score',
      size: 200,
      cell: info =>
        severityCell({
          value: info.getValue() as number,
          width: 24,
          severity: info.row.original.severity,
        }),
    },
    riskMassHeadcellV2,
    {
      id: 'asset.owner_id',
      header: 'Owner',
    },
    {
      id: `asset.source_names`,
      header: 'Sources',
      cell: info => (
        <AvItemWithLogoGroup items={(info.getValue() as string[]) || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />
      ),
    },
    {
      id: 'asset.crown_jewel',
      header: 'Crown Jewel',
    },
    {
      id: 'asset.site',
      header: 'Site',
    },
    {
      id: 'asset.first_seen',
      header: 'First Seen',
      cell: info => {
        const v = info.getValue();
        return v ? format(new Date(v as string), 'MMM dd, yyyy') : v;
      },
    },
    {
      id: 'asset.last_seen',
      header: 'Last Seen',
      cell: info => {
        const v = info.getValue();
        return v ? format(new Date(v as string), 'MMM dd, yyyy') : v;
      },
    },
    {
      id: mapAliasesAndMetrics.totalFindings.alias,
      header: 'Total Findings',
      size: 400,
      cell: info => getTotalFindingsCellComponent(info.getValue(), info.row.original),
    },
    {
      id: 'asset.tags',
      header: 'Tags',
    },
  ],
  [entityViewConfig.Finding.entityTypeId]: [
    { id: 'finding._key', header: 'ID', meta: { isKey: true } },
    { id: 'finding.type', header: 'Type', meta: { hidden: !isDebugMode() } },
    {
      id: 'finding.severity_score',
      header: 'Severity Score',
      size: 140,
      cell: info =>
        severityCell({
          value: info.getValue() as number,
          width: 24,
          severity: info.row.original['finding.severity'],
        }),
    },
    {
      id: `finding.title`,
      header: 'Title',
      size: 200,
      meta: { type: FormatterType.string },
    },
    {
      id: `finding.source_names`,
      header: 'Source',
      cell: info => (
        <AvItemWithLogoGroup items={(info.getValue() as string[]) || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />
      ),
    },
    {
      id: `asset._key`,
      header: 'Asset Key',
      meta: { type: FormatterType.string },
    },
    {
      id: `asset.type`,
      header: 'Asset Type',
      meta: { type: FormatterType.string },
    },
    {
      id: `finding.cve_id`,
      header: 'CVE',
    },
    {
      id: `finding.first_seen`,
      header: 'First Seen',
      cell: info => {
        const v = info.getValue();
        return v ? format(new Date(v as string), 'MMM dd, yyyy') : v;
      },
    },
    {
      id: `finding.last_seen`,
      header: 'Last Seen',
      cell: info => {
        const v = info.getValue();
        return v ? format(new Date(v as string), 'MMM dd, yyyy') : v;
      },
    },
    {
      id: `component.name`,
      header: 'Component',
      meta: { type: FormatterType.string },
    },
    {
      id: `finding.state`,
      header: 'State',
      cell: info => <StatusCell value={getStateValue(info.getValue() as string)} lastSeen={info.row.original[`finding.last_seen`]} />,
    },
    ...(findingsViewSlaFF ? getFindingsViewSlaHeadcells(true) : []),
  ],
});

const dynamicCellEllipsis = {
  '&.MuiTableCell-root': {
    whiteSpace: 'normal',
    '> div': {
      display: '-webkit-box',
      WebkitLineClamp: '1',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-all',
    },
  },
};

export const fieldColumnProps = {
  'finding.description': { hasTooltip: true, style: { maxWidth: 480 } },
  'finding.severity_score': {
    formatter: (value, row) => severityCell({ value, severity: row[FINDING_SEVERITY] }),
    getValue: row => `${row[FINDING_SEVERITY_SCORE]?.toFixed(1)} ${row[FINDING_SEVERITY]}`,
    initWidth: 140,
    type: 'string',
  },
  'finding.severity': {
    formatter: (severity: string) => severityCell({ severity }),
    getValue: row => `${row[FINDING_SEVERITY_SCORE]?.toFixed(1)} ${row[FINDING_SEVERITY]}`,
  },
  'alert.severity': {
    formatter: (severity: string) => severityCell({ severity }),
    getValue: row => `${row[ALERT_SEVERITY_SCORE]?.toFixed(1)} ${row[ALERT_SEVERITY]}`,
  },
  'finding.original_severity_score': {
    formatter: (value, row) => severityCell({ value, severity: row[FINDING_ORIGINAL_SEVERITY] }),
    getValue: row => `${row[FINDING_ORIGINAL_SEVERITYSCORE]?.toFixed(1)} ${row[FINDING_ORIGINAL_SEVERITY]}`,
    initWidth: 140,
    type: 'string',
  },
  'finding.original_severity': {
    formatter: severity => severityCell({ severity }),
    getValue: row => `${row[FINDING_ORIGINAL_SEVERITYSCORE]?.toFixed(1)} ${row[FINDING_ORIGINAL_SEVERITY]}`,
  },
  'finding.state': {
    formatter: (v, row) => <StatusCell value={getStateValue(v)} lastSeen={row['finding.last_seen']} />,
  },
  'finding.details_str': { formatter: v => <div>{v}</div>, style: dynamicCellEllipsis },
  'finding.asset_key': { hasTooltip: true, style: { maxWidth: 190 } },
  'finding.explore_url': {
    formatter: value => linkCell({ value, title: 'Pivot Observation' }),
    hasTooltip: true,
    style: { maxWidth: 190 },
  },
  'finding.alert_url': {
    formatter: value => linkCell({ value, title: 'View Events' }),
    hasTooltip: true,
    style: { maxWidth: 190 },
  },
  'alert.alert_link': {
    formatter: value => linkCell({ value, title: 'View Events' }),
    hasTooltip: true,
    style: { maxWidth: 190 },
  },
  'finding.last_seen': { type: FormatterType.date },
  'finding.first_seen': { type: FormatterType.date },
  'alert.last_seen': { type: FormatterType.date },
  'alert.first_seen': { type: FormatterType.date },
  'asset.type': {
    formatter: v => {
      const icon = getAssetTypeIcon(v);
      return (
        v && (
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            {icon}
            <AvTooltip>{v}</AvTooltip>
          </Box>
        )
      );
    },
  },
  'finding.source_names': {
    formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
  },
  'alert.source_names': {
    formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
  },
  'asset.source_names': {
    formatter: v => <AvItemWithLogoGroup items={v || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsed} />,
  },
  'asset.last_seen': { type: FormatterType.date, formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : v) },
  'policy_violation.severity_score': {
    formatter: (value, row) => severityCell({ value, severity: row[POLICY_VIOLATION_SEVERITY] }),
    getValue: row => `${row[POLICY_VIOLATION_SEVERITY_SCORE]?.toFixed(1)} ${row[POLICY_VIOLATION_SEVERITY]}`,
    initWidth: 140,
    type: 'string',
  },
  'alert.current_status': {
    formatter: v => <AvTag text={v} oneLine />,
  },
  'identity_finding.severity': {
    formatter: (v, row) =>
      severityCell({
        value: row['identity_finding.severity_score'],
        width: 24,
        severity: v,
      }),
  },
  'tenant.severity': {
    formatter: v =>
      severityCell({
        value: undefined,
        width: 24,
        severity: v,
      }),
  },
};

export const linkCell = ({ value, title }) =>
  isValidHttpUrl(value) ? (
    <Link href={value} target="_blank" underline="none">
      {title || value}
    </Link>
  ) : (
    value
  );

const exceptionRequesterFields = [EXCEPTION_REQUESTER_NAME, EXCEPTION_REQUESTER_EMAIL];
const exceptionReviewerFields = [EXCEPTION_REVIEWER_NAME, EXCEPTION_REVIEWER_EMAIL];

const sqlSelectors = {
  remediation: () => ['100 * (1 - ticket.active_finding_count / ticket.finding_count) as remediation'],
  'ticket.severity_score': () => ['ticket.severity_score', 'ticket.severity'],
  'incident.severity_score': () => ['incident.severity_score', 'incident.severity'],
  'alert.severity_score': () => ['alert.severity_score', 'alert.severity'],
  'finding.severity_score': () => ['finding.severity_score', 'finding.severity'],
  sla: projectionName => [projectionName === entityViewConfig.Incident.projectionName ? 'incident.sla as sla' : 'ticket.sla as sla'],
  [EXCEPTION_REQUESTER_EMAIL]: () => exceptionRequesterFields,
  [EXCEPTION_REQUESTER_NAME]: () => exceptionRequesterFields,
  [EXCEPTION_REVIEWER_EMAIL]: () => exceptionReviewerFields,
  [EXCEPTION_REVIEWER_NAME]: () => exceptionReviewerFields,
};

export const getHeadCellsIDs = (headCells: HeadCellType[] | TColumnsDef, projectionName: string, isTableV2: boolean): string[] => {
  const filteredHeadcells = isTableV2
    ? (headCells as TColumnsDef).filter(({ meta }) => !meta?.hidden)
    : (headCells as HeadCellType[]).filter(({ hidden }) => !hidden);
  return [
    ...new Set(
      filteredHeadcells
        .flatMap(({ id }) => sqlSelectors[id]?.(projectionName) || [id])
        .filter((id: string) => id.includes('.') || id.includes('remediation'))
    ),
  ];
};

export const getTableCellsByAlias = (headCells, alias) => headCells.filter(e => e.id.startsWith(`${alias}.`) || e.id === `${alias}._key`);

export const getHeadCellType = (fieldTypeMap, activeProjName, entityFieldKey) =>
  FieldTypeEnum[fieldTypeMap[`${activeProjName}.${entityFieldKey}`]] === FieldType.Number
    ? FormatterType.number
    : [FieldType.Date, FieldType.DateTime].includes(FieldTypeEnum[fieldTypeMap[`${activeProjName}.${entityFieldKey}`]] as any)
      ? FormatterType.date
      : FieldTypeEnum[fieldTypeMap[`${activeProjName}.${entityFieldKey}`]] === FieldType.Message
        ? FormatterType.json
        : Array.isArray(fieldTypeMap[`${activeProjName}.${entityFieldKey}`])
          ? FormatterType.array
          : undefined;

const NoTabDataOrFieldsFound = () => <div>No data found. Make sure the UI config is properly configured.</div>;

export function TextBlock({
  loading,
  data,
  headCells = [],
}: {
  headCells: (HeadCellType & { isJson: boolean; forceFormatter })[];
  data: any;
  loading?: boolean;
}) {
  const rows = headCells
    .map(({ id, label, hidden, isJson, forceFormatter, formatter }) =>
      hidden || !data?.[id] ? null : (
        <Box key={id} sx={{ ...flex.col, gap: '4px' }}>
          {id !== SCORE_EXPLANATION_FIELD && <Typography variant="h7">{label}</Typography>}
          <div style={{ whiteSpace: 'pre-line' }}>
            {id === SCORE_EXPLANATION_FIELD ? (
              <ScoreExplanation
                data={data[id]}
                severityData={{
                  severity: data[FINDING_SEVERITY],
                  score: data[FINDING_SEVERITY_SCORE],
                }}
              />
            ) : forceFormatter ? (
              // @ts-ignore
              formatter?.(data[id])
            ) : isJson ? (
              <AvJsonViewer data={isJSON(data[id]) ? data[id] : JSON.parse(data[id])} />
            ) : typeof data[id] === 'boolean' ? (
              <AvTag text={data[id] ? 'True' : 'False'} />
            ) : (
              data[id]
            )}
          </div>
        </Box>
      )
    )
    .filter(Boolean);

  return (
    <Box sx={{ ...flex.col, gap: 3, py: 2 }}>
      {loading ? (
        <>
          <Skeleton variant="text" height={21} width="50%" />
          <Skeleton variant="text" height={21} width="35%" />
        </>
      ) : rows.length ? (
        rows
      ) : (
        <NoTabDataOrFieldsFound />
      )}
    </Box>
  );
}

/** * SORTING LOGIC: took it from: https://www.npmjs.com/package/tiny-version-compare ** */
const splitDev = v => String(v).split('-');

const splitSub = v =>
  String(v)
    .replace(/^[vr]/, '') // Drop initial 'v' or 'r'
    .replace(/([a-z]+)/gi, '.$1.') // Sort each word separately
    .replace(/\.+/g, '.') // Trim repeating separators
    .split('.');

// Development versions are considered "negative",
// but localeCompare doesn't handle negative numbers.
// This offset is applied to reset the lowest development version to 0
const offset = part => {
  // Not numeric, return as is
  if (Number.isNaN(part)) {
    return part;
  }

  return 5 + Number(part);
};

const parseSub = part => {
  // Missing, consider it zero
  if (typeof part === 'undefined') {
    return 0;
  }

  // Sort development versions
  switch (part.toLowerCase()) {
    case 'dev':
      return -5;
    case 'alpha':
    case 'a':
      return -4;
    case 'beta':
    case 'b':
      return -3;
    case 'rc':
    case 'c':
      return -2;
    case 'pre':
      return -1;
    default:
  }

  // Return as is, it’s either a plain number or text that will be sorted alphabetically
  return part;
};

function compareSubs(a, b) {
  // eslint-disable-next-line custom-rules/no-let
  for (let i = 0; i < a.length || i < b.length; i++) {
    const ai = offset(parseSub(a[i]));
    const bi = offset(parseSub(b[i]));
    const sort = String(ai).localeCompare(bi, 'en', {
      numeric: true,
    });

    // Once the difference is found,
    // stop comparing the rest of the parts
    if (sort !== 0) {
      return sort;
    }
  }

  return 0;
}

export function compareVersions(a, b) {
  if (a === b) {
    return 0;
  }

  const [aMain, aDev] = splitDev(a).map(splitSub);
  const [bMain, bDev] = splitDev(b).map(splitSub);

  const mainSort = compareSubs(aMain, bMain);
  if (mainSort !== 0) {
    return mainSort;
  }

  if (aDev && !bDev) {
    return -1;
  }

  if (!aDev && bDev) {
    return 1;
  }

  if (aDev && bDev) {
    return compareSubs(aDev, bDev);
  }

  // No difference found
  return 0;
}
